/**
 * Text formatting of cells and column headers (UD-UX3), pure and unit-tested: the closed
 * vocabularies the server accepts (`app/databases/styles.py`), the safe CSS they turn into, and
 * the per-property inheritance. Formatting is presentation only - never part of a value.
 */
import type { CSSProperties } from 'react';

import { contrastRatio, contrastText, isHexColor } from '@/shared/lib/color';

import { isFormatColor } from './format';
import type {
  CellStyle,
  DatabaseColumn,
  DatabaseRecord,
  HeaderStyle,
  StyleAlign,
  StyleFont,
  StylePatch,
  StyleVAlign,
} from './types';

/** Named families first (what a spreadsheet offers), then the generic keys of UD-UX3. */
export const STYLE_FONTS: readonly StyleFont[] = [
  'arial',
  'times',
  'calibri',
  'georgia',
  'verdana',
  'tahoma',
  'trebuchet',
  'courier',
  'sans',
  'serif',
  'mono',
];
/** The human name of a named family (a proper name, never translated); generic keys have none. */
export const FONT_NAMES: Partial<Record<StyleFont, string>> = {
  arial: 'Arial',
  times: 'Times New Roman',
  calibri: 'Calibri',
  georgia: 'Georgia',
  verdana: 'Verdana',
  tahoma: 'Tahoma',
  trebuchet: 'Trebuchet MS',
  courier: 'Courier New',
};
/**
 * Safe system font stacks: no web fonts are loaded and no free text reaches CSS. A named family is
 * used when the device has it; otherwise a metric-compatible or generic one stands in (Liberation
 * and Carlito on Linux, DejaVu), so a font is never promised to exist everywhere.
 */
export const FONT_STACKS: Record<StyleFont, string> = {
  sans: 'ui-sans-serif, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  arial: 'Arial, "Liberation Sans", Helvetica, sans-serif',
  times: '"Times New Roman", "Liberation Serif", Times, serif',
  calibri: 'Calibri, Carlito, "Segoe UI", sans-serif',
  georgia: 'Georgia, "DejaVu Serif", serif',
  verdana: 'Verdana, "DejaVu Sans", Geneva, sans-serif',
  tahoma: 'Tahoma, "DejaVu Sans", Geneva, sans-serif',
  trebuchet: '"Trebuchet MS", "Lucida Grande", "DejaVu Sans", sans-serif',
  courier: '"Courier New", "Liberation Mono", Courier, monospace',
};
/** A text size is any whole number of px in this range (the server enforces the same). */
export const MIN_SIZE = 8;
export const MAX_SIZE = 72;
/** Nearby sizes offered as suggestions; typing a number never snaps to one of them. */
export const SIZE_PRESETS = [10, 11, 12, 14, 16, 18, 20, 24, 28] as const;
/** The size a cell has without an explicit one (the grid's own text size). */
export const DEFAULT_SIZE = 14;
export const STYLE_ALIGNS: readonly StyleAlign[] = ['left', 'center', 'right'];
export const STYLE_VALIGNS: readonly StyleVAlign[] = ['top', 'middle', 'bottom'];
export const STYLE_KEYS = ['font', 'size', 'bold', 'italic', 'color', 'align', 'valign'] as const;
export type StyleKey = (typeof STYLE_KEYS)[number];
/** Every text property handed back to what it inherits ("reset text format"). */
export const RESET_PATCH: StylePatch = {
  font: null,
  size: null,
  bold: null,
  italic: null,
  color: null,
  align: null,
  valign: null,
};

/** Line height used for a text size (20 px at the default size, as before this stage). */
export const lineHeightOf = (size: number = DEFAULT_SIZE): number =>
  size === DEFAULT_SIZE ? 20 : Math.round(size * 1.45);

const FLEX = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;
const VFLEX = { top: 'flex-start', middle: 'center', bottom: 'flex-end' } as const;

/** Whether `value` is a size the vocabulary allows: a whole number of px in range. */
export const isTextSize = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= MIN_SIZE && value <= MAX_SIZE;

/** A style value only when it is one the vocabulary knows (a malformed stored one is ignored). */
export function safeStyle<T extends CellStyle>(style: T | undefined): T | undefined {
  if (!style) return undefined;
  const clean: CellStyle = {};
  if (style.font && STYLE_FONTS.includes(style.font)) clean.font = style.font;
  if (isTextSize(style.size)) clean.size = style.size;
  if (typeof style.bold === 'boolean') clean.bold = style.bold;
  if (typeof style.italic === 'boolean') clean.italic = style.italic;
  if (isHexColor(style.color)) clean.color = style.color.toLowerCase();
  if (style.align && STYLE_ALIGNS.includes(style.align)) clean.align = style.align;
  if (style.valign && STYLE_VALIGNS.includes(style.valign)) clean.valign = style.valign;
  const background = (style as HeaderStyle).background;
  if (isHexColor(background)) (clean as HeaderStyle).background = background.toLowerCase();
  if (typeof (style as HeaderStyle).wrap === 'boolean') {
    (clean as HeaderStyle).wrap = (style as HeaderStyle).wrap as boolean;
  }
  return Object.keys(clean).length > 0 ? (clean as T) : undefined;
}

export const cellStyleOf = (
  record: Pick<DatabaseRecord, 'cell_styles'>,
  columnId: string,
): CellStyle | undefined => safeStyle(record.cell_styles[columnId]);

/** The explicit background of a CELL when it is not a palette color (drawn exactly as chosen). */
export function customFill(color: string | null | undefined): string | null {
  if (typeof color !== 'string' || !isHexColor(color)) return null;
  const lower = color.toLowerCase();
  return isFormatColor(lower) ? null : lower;
}

/**
 * CSS of a formatted cell. Font, size, weight, slant and color sit on the cell itself and are
 * inherited by whatever it shows (text, dots, badges, pills keep their own colors). Alignment is
 * `text-align` plus two custom properties for the flex rows of choices and people; a vertical
 * alignment turns the cell into a column flex box so the content sits where it was asked to.
 * `background` is the explicit custom fill, if any: without an explicit text color the text then
 * gets the readable one (the user's own colors are never replaced).
 */
export function cellCss(
  style: CellStyle | undefined,
  background?: string | null,
  { vertical = true }: { vertical?: boolean } = {},
): CSSProperties | undefined {
  const css: Record<string, string | number> = {};
  if (style?.font) css['fontFamily'] = FONT_STACKS[style.font];
  if (style?.size) {
    css['fontSize'] = `${String(style.size)}px`;
    css['lineHeight'] = `${String(lineHeightOf(style.size))}px`;
    css['--cell-lh'] = `${String(lineHeightOf(style.size))}px`;
  }
  if (style?.bold !== undefined) css['fontWeight'] = style.bold ? 700 : 400;
  if (style?.italic !== undefined) css['fontStyle'] = style.italic ? 'italic' : 'normal';
  if (style?.color) css['color'] = style.color;
  else if (background) css['color'] = contrastText(background);
  if (style?.align) {
    css['textAlign'] = style.align;
    css['--cell-h'] = FLEX[style.align];
    css['--cell-ta'] = style.align;
  }
  if (style?.valign && vertical) {
    css['display'] = 'flex';
    css['flexDirection'] = 'column';
    css['justifyContent'] = VFLEX[style.valign];
    // The cross axis of that column is the HORIZONTAL one: without this a child that is a flex box
    // of its own (a choice, a tag) stretches and ignores the horizontal alignment.
    if (style.align) css['alignItems'] = FLEX[style.align];
    css['--cell-v'] = VFLEX[style.valign];
    css['--cell-fill'] = 'auto';
    css['paddingBlock'] = '4px';
    css['lineHeight'] = `${String(lineHeightOf(style.size))}px`;
  }
  return Object.keys(css).length > 0 ? css : undefined;
}

/**
 * CSS of a column header NAME (the icons, the sort mark and the menu keep their own look and place):
 * font, size, weight, slant, color, text alignment and its vertical position in the header. With
 * only a header background set, the text gets the readable color.
 */
export function headerCss(style: HeaderStyle | undefined): CSSProperties | undefined {
  if (!style) return undefined;
  const css: Record<string, string | number> = {};
  if (style.font) css['fontFamily'] = FONT_STACKS[style.font];
  if (style.size) {
    css['fontSize'] = `${String(style.size)}px`;
    css['lineHeight'] = `${String(lineHeightOf(style.size))}px`;
  }
  if (style.bold !== undefined) css['fontWeight'] = style.bold ? 700 : 400;
  if (style.italic !== undefined) css['fontStyle'] = style.italic ? 'italic' : 'normal';
  if (style.color) css['color'] = style.color;
  else if (style.background) css['color'] = contrastText(style.background);
  if (style.align) css['textAlign'] = style.align;
  return Object.keys(css).length > 0 ? css : undefined;
}

/**
 * Where the TITLE GROUP (field icon + name, one unit) sits in the header: horizontally through the
 * flex main axis, vertically through `alignSelf`. The sort / filter marks and the menu stay put.
 */
export function headerTitleCss(style: HeaderStyle | undefined): CSSProperties | undefined {
  if (!style?.align && !style?.valign) return undefined;
  const css: Record<string, string> = {};
  if (style.align) css['justifyContent'] = FLEX[style.align];
  if (style.valign) css['alignSelf'] = VFLEX[style.valign];
  return css;
}

/** A property as it applies to several cells: the shared value, or `mixed`, or unset. */
export type Effective<T> = { state: 'value'; value: T } | { state: 'unset' } | { state: 'mixed' };

export function effectiveOf<K extends StyleKey>(
  styles: readonly (CellStyle | undefined)[],
  key: K,
): Effective<NonNullable<CellStyle[K]>> {
  let seen: NonNullable<CellStyle[K]> | undefined;
  let first = true;
  for (const style of styles) {
    const value = style?.[key] as NonNullable<CellStyle[K]> | undefined;
    if (first) {
      seen = value;
      first = false;
    } else if (value !== seen) {
      return { state: 'mixed' };
    }
  }
  return seen === undefined ? { state: 'unset' } : { state: 'value', value: seen };
}

/** `style` after `patch` the way the server merges it (`null` removes, the rest is kept). */
export function mergeStyle<T extends CellStyle>(
  style: T | undefined,
  patch: StylePatch<T>,
): T | undefined {
  const next = new Map<string, unknown>(Object.entries(style ?? {}));
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) next.delete(key);
    else if (value !== undefined) next.set(key, value);
  }
  return next.size > 0 ? (Object.fromEntries(next) as unknown as T) : undefined;
}

/** Contrast of explicit text over an explicit background (`null` when either is not explicit). */
export function styleContrast(
  text: string | null | undefined,
  background: string | null | undefined,
): number | null {
  return isHexColor(text) && isHexColor(background) ? contrastRatio(text, background) : null;
}

/** Hard to read: below the WCAG ratio for large text. */
export const POOR_CONTRAST = 3;

export const headerStyleOf = (column: Pick<DatabaseColumn, 'header_style'>) =>
  safeStyle(column.header_style as HeaderStyle | undefined);
